import {
  sendNotification,
  useIDTagging,
  useMeasurementUnitsList,
  useTranslation,
  useViewBaseMetadata,
} from '@msdk/common'
import {
  Entity,
  EntityMetadata,
  getConversionUM,
  NumericFieldDefinition,
  ViewColumnDefinition,
  ViewColumnDetail,
  ViewModel,
} from '@msdk/definitions'
import React, {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react'

import { useResizeWindows } from '../../../hooks/window-resize'
import { getFieldDefIncludeExternal } from '../../../utils'
import { filterableColumns } from '../FilterPanel'
import { sortableColumns } from '../FilterPanel/sortable-cols'
import { useColumnsLayoutComparer } from '../hooks/useColumnsLayoutComparer'
import { usePersistedColumnsLayout } from '../hooks/usePersistedColumnsLayout'
import { CustomViewColumnDefinition, DynamicViewContextProps, DynamicViewContextType } from './types'

type ColumnType = CustomViewColumnDefinition & { currentWidth?: string; measuredWidth?: string }

const DynamicViewContext = createContext<DynamicViewContextType | undefined>(undefined)

const SETTINGS = {
  colMinWidth: 50,
  extraColDefaultWidth: 150,
  shouldResizeColumnsOnToggleSelect: true,
  shouldResizeColumnsOnToggleShowExtra: false,
  shouldResizeColumnsOnToggleActions: true,
  shouldResizeColumnsOnToggleIdTagging: true,
}

const parseWidth = (width?: string) => {
  if (width?.includes('px') && Number(width.split('px')[0]) < SETTINGS.colMinWidth) {
    return `${SETTINGS.colMinWidth}px`
  }
  return width
}

function getExpectedWidth(idTaggingEnabled: boolean, column: ViewColumnDefinition, viewModel: ViewModel) {
  return idTaggingEnabled ? undefined : column.width || viewModel.defaultColumnStyle?.width
}

export function DynamicViewProvider({
  children,
  entityModel,
  onOrderChange,
  viewModel,
  rows,
  reload,
  isLoading,
  onRowClick,
  multiSelect,
  selectedRows,
  onRowSelected,
  styleMode,
  acl,
  addColumnFilter,
  removeColumnFilter,
  removeAllColumnFilter,
  additionalCondition,
  currentOrder,
  modalRef,
  currentFilters,
  behaviour,
  hideHeader,
  isEmpty,
  showExtra,
  hideTopBorder,
  areRowsClickable,
  isEmbedded,
  addOrUpdateFilter,
  onDataChanged,
  refreshView,
}: DynamicViewContextProps) {
  const t = useTranslation()
  const hasSameColumnsLayout = useColumnsLayoutComparer()
  const idTaggingEnabled = useIDTagging()

  const baseMetadata = useViewBaseMetadata(entityModel.name, viewModel.name) as EntityMetadata

  const tableRef = useRef<HTMLTableElement>(null)

  const initialColumnsSet = viewModel.columns.map((column) => {
    const expectedWidth = getExpectedWidth(idTaggingEnabled, column, viewModel)
    const normalizedWidth = expectedWidth?.includes('%') ? undefined : expectedWidth
    return {
      ...column,
      id: column.fieldName,
      width: normalizedWidth as string,
      currentWidth: normalizedWidth,
      measuredWidth: undefined,
      visibility: true,
    }
  })

  const vmOrder = viewModel.order?.[0]
  const initialOrderBy = vmOrder?.fieldName || ''
  const initialOrder = vmOrder?.type ? (vmOrder.type.toLowerCase() as 'asc' | 'desc') : 'asc'

  const defaultOrder = useMemo(() => {
    let initialBy: string | undefined = initialOrderBy
    if (!initialBy && currentOrder) {
      initialBy = currentOrder.split(':')?.at(0)
    }
    return initialBy ? `${initialBy}:${initialOrder}` : undefined
  }, [currentOrder])

  const [order, setOrder] = useState<'asc' | 'desc'>(initialOrder)
  const [orderBy, setOrderBy] = useState(initialOrderBy)

  /**
   * Enriched columns with default values.
   * - `id` (required for d&d)
   * - `width`
   * - `visibility` (this is a different property from the `hidden` property in the column definition.
   *   `hidden` is infrastructural, this only handles showing/hiding infrastructurally visible columns)
   */
  const [columns, setColumns] = useState<ColumnType[]>(initialColumnsSet)

  /**
   * Extract only real visible columns.
   * This filters out infrastructurally hidden columns and client hidden columns.
   *
   * This is used for rendering the table.
   */
  const visibleColumns = useMemo(() => columns.filter((column) => !column.hidden), [columns])

  const [didMeasuredColumns, setDidMeasuredColumns] = useState(false)

  const [rowActionsMap, setRowActionsMap] = useState<Record<string, boolean>>({})

  const anyRowHasActions = useMemo(() => {
    return Object.values(rowActionsMap).some((hasAction) => hasAction)
  }, [rowActionsMap])

  const measureCols = useCallback(() => {
    if (!tableRef.current) return
    const headerCells = Array.from(tableRef.current.querySelectorAll('th'))
    const updatedCols = columns.map((c) => {
      const cell = headerCells.find((v) => v.getAttribute('data-col-name') === c.id)
      const width = cell?.getBoundingClientRect().width
      const measuredWidth = parseWidth(width ? `${width + (idTaggingEnabled ? 32 : 0)}px` : undefined)
      return { ...c, measuredWidth }
    })

    setColumns(updatedCols)
  }, [columns])

  const [isColumnsLayoutInit, setColumnsLayoutInit] = useState(false)
  const {
    isPersistedLayoutInit,
    persistedLayout,
    canPersistColumnsLayout,
    matchesPersistedColumnsLayout,
    removePersistedColumnsLayout,
    syncToPersistedColumnsLayout,
    setPersistedColumnsLayout,
  } = usePersistedColumnsLayout(viewModel.name)

  const { width: windowsWidth } = useResizeWindows(100)

  /**
   * The default table state indicates the absence of filtering, system or hidden columns.
   */
  const isDefaultTable = useMemo(() => {
    let isDefault = true
    if (isPersistedLayoutInit && persistedLayout) {
      isDefault = canPersistColumnsLayout
        ? matchesPersistedColumnsLayout(initialColumnsSet)
        : matchesPersistedColumnsLayout(columns)
    } else {
      isDefault = hasSameColumnsLayout(initialColumnsSet, visibleColumns)
    }

    isDefault =
      isDefault &&
      !visibleColumns?.some((column) => column.currentWidth !== getExpectedWidth(idTaggingEnabled, column, viewModel))

    isDefault = isDefault && !additionalCondition?.conditions?.length
    isDefault = isDefault && (!currentOrder || currentOrder === defaultOrder)

    return isDefault
  }, [defaultOrder, visibleColumns, additionalCondition, currentOrder, isPersistedLayoutInit, canPersistColumnsLayout])

  useLayoutEffect(() => {
    if (!didMeasuredColumns) {
      measureCols()
      setDidMeasuredColumns(true)
    }
  }, [didMeasuredColumns, measureCols])

  useLayoutEffect(() => {
    if (isPersistedLayoutInit) {
      setColumns((cs) => {
        const synced = syncToPersistedColumnsLayout(cs)
        if (!synced) {
          return cs
        }
        return synced.map((c) => ({
          ...c,
          measuredWidth: undefined, // will permit the re-mesourment
        }))
      })
      setDidMeasuredColumns(false)
      setColumnsLayoutInit(true)
    }
  }, [isPersistedLayoutInit])

  const resetMeasurements = (resetCurrentWidth?: boolean) => {
    setColumns((cs) =>
      cs.map((c) => {
        return {
          ...c,
          measuredWidth: resetCurrentWidth ? undefined : c.currentWidth,
          currentWidth: resetCurrentWidth ? undefined : c.currentWidth,
        }
      }),
    )
    setDidMeasuredColumns(false)
  }

  useEffect(() => {
    if (!SETTINGS.shouldResizeColumnsOnToggleActions) {
      return
    }
    resetMeasurements()
  }, [anyRowHasActions])

  useEffect(() => {
    if (!SETTINGS.shouldResizeColumnsOnToggleSelect) {
      return
    }
    resetMeasurements()
  }, [multiSelect])

  const [isEnabled, setEnabled] = useState(idTaggingEnabled)
  useEffect(() => {
    if (isEnabled !== idTaggingEnabled) {
      setEnabled(idTaggingEnabled)
      resetMeasurements(true)
    }
  }, [idTaggingEnabled, isEnabled])

  useEffect(() => {
    resetMeasurements()
  }, [windowsWidth])

  useEffect(() => {
    setColumns((cs) =>
      cs.map((c) => {
        const width = c.currentWidth ?? `${SETTINGS.extraColDefaultWidth}px`
        return {
          ...c,
          currentWidth: c.detail === ViewColumnDetail.Extra ? width : c.currentWidth,
          measuredWidth: c.detail === ViewColumnDetail.Extra ? width : c.measuredWidth,
        }
      }),
    )
    if (!SETTINGS.shouldResizeColumnsOnToggleShowExtra) {
      return
    }
    resetMeasurements()
  }, [showExtra])

  const mus = useMeasurementUnitsList()

  const getColumnHeaderInfo = (column: ViewColumnDefinition) => {
    const fieldDef = getFieldDefIncludeExternal(viewModel, column.fieldName, baseMetadata)
    const desc = column.overrideDescription || fieldDef?.description || ''
    const idml = column.overrideIDML || fieldDef?.idml || ''

    if (viewModel.hideColumnHeaderSubtitle) {
      return {
        title: t(idml, desc),
      }
    }

    let sub = ''
    if (fieldDef?.fieldDefinitionType === 'Numeric') {
      const numeric = fieldDef as NumericFieldDefinition

      if (numeric.customMeasurementUnit) {
        sub = t('CMPS_VARIABLE_UNIT', 'Variable unit')
      } else {
        const conversionUm = getConversionUM(numeric.um, numeric.entityName, numeric.name) ?? numeric.um
        const muIDML = mus?.find((u) => u.key === conversionUm)?.idml ?? conversionUm
        sub = conversionUm ? `(${t(muIDML, conversionUm)})` : ''
      }
    }

    return {
      title: t(idml, desc),
      subtitle: sub,
    }
  }

  const onSelectAllRows = (toggle: boolean) => {
    if (onRowSelected && toggle) onRowSelected(rows as Entity[])
    else if (onRowSelected && !toggle) onRowSelected([])
  }

  const handleOrderChange = (columnName: string, orderToSet?: 'asc' | 'desc') => {
    let newOrder: 'asc' | 'desc' = order === 'asc' ? 'desc' : 'asc'
    newOrder = orderToSet ?? newOrder
    setOrderBy(columnName)
    setOrder(newOrder)
    onOrderChange(columnName, newOrder)
  }

  /**
   * Get the list of columns that are sortable.
   */
  const sortColumns = sortableColumns(viewModel, baseMetadata)

  /**
   * Get the list of columns that are filterable.
   */
  const filterColumns = filterableColumns(viewModel, baseMetadata)

  const handleRowHasActions = (rowId: string, hasActions: boolean) => {
    setRowActionsMap((prev) => ({
      ...prev,
      [rowId]: hasActions,
    }))
  }

  function toggleColumnVisibility(column: CustomViewColumnDefinition) {
    setColumns((cols) =>
      cols.map((c) => ({
        ...c,
        visibility: c.id === column.id ? !c.visibility : c.visibility,
      })),
    )
  }

  /**
   * Handles updating the width of a column when resizing it.
   */
  const handleColumnResize = (columnId: string, newWidth: string) => {
    const width = parseWidth(newWidth)
    setColumns((prevColumns) =>
      prevColumns.map((column) =>
        column.id === columnId ? { ...column, currentWidth: width, measuredWidth: width } : column,
      ),
    )
  }

  /**
   * Returns the width to assign to the "empty" column used to stabilize
   * the initial layout and resizing behavior of the table.
   *
   * Why does this logic exist?
   * - The table includes an invisible "empty column" that acts as a flexible
   *   spacer when the user resizes real columns.
   * - On the initial render, all visible columns must receive an evenly
   *   distributed width from the browser (e.g., 4 columns → 25% each).
   *   However, if the empty column has an undefined width at this stage,
   *   the browser treats it like a real column and includes it in the
   *   distribution (e.g., 5 columns → 20% each). This is incorrect.
   *
   * - To prevent this, until all real columns have been measured,
   *   the empty column is assigned **"0px"**. This ensures that:
   *     1) The browser ignores the empty column when calculating initial widths.
   *     2) Real columns get the correct proportional size.
   *
   * - Once all real columns have explicit pixel widths, the empty column
   *   can be set back to width=undefined, allowing it to act as a flexible
   *   space absorber during user-driven resizing without affecting the
   *   overall table width.
   *
   * DO NOT remove this logic:
   * removing "0px" and always using width=undefined will introduce a bug
   * where the empty column receives an equal share of the width, shrinking
   * all real columns (e.g., 25% → 20%).
   */
  const getFreeSpace = () => {
    const allColsAreMeasured = visibleColumns
      .filter(
        (c) =>
          c.visibility && (c.detail !== ViewColumnDetail.Extra || (c.detail === ViewColumnDetail.Extra && showExtra)),
      )
      .every((c) => !!c.measuredWidth)

    return allColsAreMeasured ? undefined : '0px'
  }

  const getColumnWidth = (fieldName: string) => {
    const column = visibleColumns.find((c) => c.fieldName === fieldName)
    return column?.currentWidth || column?.measuredWidth
  }

  async function resetToDetaultTable() {
    try {
      // Resets to the initial or (admin) persisted columns set
      const synchedInitLayout = syncToPersistedColumnsLayout(initialColumnsSet)
      setColumns(synchedInitLayout && !canPersistColumnsLayout ? synchedInitLayout : initialColumnsSet)

      if (canPersistColumnsLayout) {
        await removePersistedColumnsLayout()
      }

      setOrder(initialOrder)
      setOrderBy(initialOrderBy)

      await removeAllColumnFilter(defaultOrder)

      setDidMeasuredColumns(false)

      sendNotification({
        message: t('LBL_TABLE_MANAGEMENT_RESET_TABLE_SUCCESS', 'The table has been reset to default state'),
        status: 'success',
      })
    } catch (ex) {
      sendNotification({
        message: t('LBL_TABLE_MANAGEMENT_RESET_TABLE_FAILURE', 'Failed to reset the table to default state'),
        status: 'error',
      })
    }
  }

  const value = {
    viewModel,
    baseMetadata,
    entityModel,
    columns,
    onOrderChange,
    reload,
    rows,
    isLoading,
    onRowClick,
    multiSelect,
    selectedRows,
    onRowSelected,
    styleMode,
    acl,
    addColumnFilter,
    removeColumnFilter,
    removeAllColumnFilter,
    additionalCondition,
    modalRef,
    currentFilters,
    behaviour,
    hideHeader,
    isEmpty,
    onSelectAllRows,
    rowActionsMap,
    setRowActionsMap,
    anyRowHasActions,
    updateColumns: setColumns,
    order,
    orderBy,
    handleOrderChange,
    sortColumns,
    filterColumns,
    handleRowHasActions,
    visibleColumns,
    toggleColumnVisibility,
    handleColumnResize,
    tableRef,
    showExtra,
    pinned: viewModel.pinned,
    hideTopBorder,
    getFreeSpace,
    getColumnWidth,
    areRowsClickable,
    didMeasuredColumns,
    getColumnHeaderInfo,
    isEmbedded,
    isDefaultTable,
    resetToDetaultTable,
    addOrUpdateFilter,
    isColumnsLayoutInit,
    canPersistColumnsLayout,
    setPersistedColumnsLayout,
    onDataChanged,
    refreshView,
  }

  return <DynamicViewContext.Provider value={value}>{children}</DynamicViewContext.Provider>
}

export function useDynamicViewContext() {
  const context = useContext(DynamicViewContext)

  if (context === undefined) {
    throw new Error('useDynamicViewContext must be used within a DynamicViewProvider')
  }

  return context
}
